import { useCallback, useEffect, useState } from "react";
import { Building2, FileCheck2, Plus, Search, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { Shell } from "../components/Shell";
import { CopyField } from "../components/CopyField";
import { EditDialog } from "../components/EditDialog";
import { ResetPasswordButton } from "../components/ResetPasswordButton";
import { PlanCheckoutButton } from "../components/PlanCheckoutButton";
import { BillingHistoryButton } from "../components/BillingHistoryButton";
import { RegistryReportButton } from "../components/RegistryReportButton";
import { ChangePasswordCard } from "../components/ChangePasswordCard";
import { StripeCatalogCard } from "../components/StripeCatalogCard";
import { SystemKeysCard } from "../components/SystemKeysCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
    <div className="flex items-center justify-between">
      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
        {label}
      </div>
      <Icon className="h-4 w-4 text-blue-600" />
    </div>
    <div className="mt-4 font-display text-4xl font-black tracking-tight text-slate-900" data-testid={testId}>
      {value}
    </div>
  </div>
);

const PLAN_BADGE_CLASS = {
  paid: "bg-emerald-50 text-emerald-700",
  pending: "bg-blue-50 text-blue-700",
  free: "bg-amber-50 text-amber-700",
  trial: "bg-amber-50 text-amber-700",
};

const planBadgeClass = (c) => PLAN_BADGE_CLASS[c.payment_status] || "bg-slate-100 text-slate-600";

const planBadgeLabel = (c) => {
  if (c.payment_status === "paid") return `${c.plan_name} · paid`;
  if (c.payment_status === "pending") return "Payment pending";
  if (c.payment_status === "free" || c.payment_status === "trial")
    return `Free · ${c.active_customers}/${c.applications_limit} applications`;
  return "No plan";
};

export default function SuperAdmin() {
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({});
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [stripeRefresh, setStripeRefresh] = useState(0);
  const [form, setForm] = useState({
    company_name: "",
    admin_name: "",
    admin_email: "",
    admin_phone: "",
  });

  const load = useCallback(async () => {
    const [c, s] = await Promise.all([api.get("/admin/companies"), api.get("/admin/stats")]);
    setCompanies(c.data);
    setStats(s.data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
    api
      .get("/plans")
      .then(({ data }) => setPlans(data))
      .catch(() => {});
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/companies", {
        ...form,
        origin_url: window.location.origin,
      });
      toast.success(
        data.sent
          ? `${data.name} onboarded. Setup email sent to ${data.admin_email}.`
          : `${data.name} onboarded, but the welcome email failed: ${data.error}. Password: ${data.temp_password}`
      );
      setOpen(false);
      setForm({ company_name: "", admin_name: "", admin_email: "", admin_phone: "" });
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const removeCompany = async (c) => {
    if (
      !window.confirm(
        `Delete ${c.name}? This also removes their customers, references and logins. This cannot be undone.`
      )
    )
      return;
    try {
      await api.delete(`/admin/companies/${c.id}`);
      toast.success(`${c.name} removed`);
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const purgeAll = async () => {
    if (
      !window.confirm(
        "Purge EVERY company, customer and reference from ConfirmTerms? Your super admin login is kept. This cannot be undone."
      )
    )
      return;
    try {
      const { data } = await api.delete("/admin/companies/purge-all");
      toast.success(
        `Purged ${data.companies_removed} companies, ${data.customers_removed} customers, ${data.references_removed} references`
      );
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const field = (key, label, type = "text", required = true) => (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </Label>
      <Input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) =>
          setForm({ ...form, [key]: type === "tel" ? formatPhone(e.target.value) : e.target.value })
        }
        data-testid={`company-${key}-input`}
        className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
      />
    </div>
  );

  const q = query.trim().toLowerCase();
  const filtered = q
    ? companies.filter((c) =>
        [c.name, c.admin_name, c.admin_email].some((v) => (v || "").toLowerCase().includes(q))
      )
    : companies;

  return (
    <Shell
      title="Registered Companies / ConfirmTerms Clients"
      portalLabel="Admin Portal"
      roleLine="You own ConfirmTerms and onboard the companies that use it to check their customers' credit references."
      actions={
        <div className="flex flex-wrap items-center gap-3">
          <RegistryReportButton />
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-company-button"
              className="h-11 rounded-full bg-blue-600 px-6 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
                Onboard a new company
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-5" data-testid="company-form">
              {field("company_name", "Company name")}
              {field("admin_name", "Admin contact name")}
              {field("admin_email", "Admin email", "email")}
              {field("admin_phone", "Admin phone", "tel", false)}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="company-submit-button"
                  className="h-11 w-full rounded-md bg-blue-600 font-semibold hover:bg-blue-700"
                >
                  {busy ? "Creating…" : "Create company & admin login"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      }
    >
      <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
        <Stat icon={Building2} label="Companies" value={stats.companies ?? 0} testId="stat-companies" />
        <Stat icon={Users} label="Customers" value={stats.customers ?? 0} testId="stat-customers" />
        <Stat icon={FileCheck2} label="References" value={stats.references ?? 0} testId="stat-references" />
        <Stat
          icon={FileCheck2}
          label="Completed"
          value={stats.completed_references ?? 0}
          testId="stat-completed"
        />
      </div>

      {companies.length > 0 && (
        <div className="relative mb-6 max-w-md">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by company, admin name or email…"
            data-testid="company-search-input"
            className="h-11 border-slate-300 pl-10 focus-visible:ring-blue-600"
          />
        </div>
      )}

      {companies.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center"
          data-testid="companies-empty"
        >
          <p className="font-display text-lg font-bold text-slate-900">No companies yet</p>
          <p className="mt-2 text-sm text-slate-500">
            Create your first company to issue an administrator login.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="companies-list">
          {filtered.length === 0 && (
            <div
              className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500"
              data-testid="company-search-no-results"
            >
              No companies match &ldquo;{query}&rdquo;.
            </div>
          )}
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`company-row-${c.id}`}
              className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition-colors duration-200 hover:border-blue-300"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-xl font-bold tracking-tight text-slate-900">
                    {c.name}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {c.admin_name} · {c.admin_email}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {c.customers} customer{c.customers === 1 ? "" : "s"}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${planBadgeClass(c)}`}
                    data-testid={`company-payment-${c.id}`}
                  >
                    {planBadgeLabel(c)}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      c.has_w9 ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {c.has_w9 ? "W-9 on file" : "W-9 missing"}
                  </span>
                  <PlanCheckoutButton
                    testId={`plan-${c.id}`}
                    companyId={c.id}
                    companyName={c.name}
                    plans={plans}
                    paid={c.payment_status === "paid"}
                  />
                  <BillingHistoryButton
                    testId={`billing-${c.id}`}
                    companyId={c.id}
                    companyName={c.name}
                  />
                  <EditDialog
                    testId={`edit-company-${c.id}`}
                    title="Edit company profile"
                    endpoint={`/admin/companies/${c.id}`}
                    onSaved={load}                    fields={[
                      ["company_name", "Company name"],
                      ["admin_name", "Admin contact name"],
                      ["admin_email", "Admin email", "email"],
                      ["admin_phone", "Admin phone", "tel", false],
                    ]}
                    initial={{
                      company_name: c.name,
                      admin_name: c.admin_name || "",
                      admin_email: c.admin_email || "",
                      admin_phone: c.admin_phone || "",
                    }}
                  />
                  <ResetPasswordButton
                    testId={`reset-company-${c.id}`}
                    label={`${c.admin_email}`}
                    endpoint={`/admin/companies/${c.id}/reset-password`}
                    onDone={load}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`delete-company-${c.id}`}
                    onClick={() => removeCompany(c)}
                    className="rounded-full border-red-200 text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
              {c.temp_password && (
                <div className="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                  <CopyField label="Admin login" value={c.admin_email} testId={`company-${c.id}-email`} />
                  <CopyField
                    label="Generated password"
                    value={c.temp_password}
                    testId={`company-${c.id}-password`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {companies.length > 0 && (
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50/60 p-6">
          <div>
            <p className="font-display text-base font-bold text-red-900">Danger zone</p>
            <p className="mt-1 text-sm text-red-700">
              Permanently remove every company, customer and reference. Your super admin login is
              kept.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={purgeAll}
            data-testid="purge-all-companies-button"
            className="rounded-full border-red-300 bg-white text-red-700 transition-colors duration-200 hover:bg-red-100"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Purge all companies
          </Button>
        </div>
      )}

      <div className="mt-10 space-y-6">
        <SystemKeysCard onSaved={() => setStripeRefresh((n) => n + 1)} />
        <StripeCatalogCard key={stripeRefresh} />
      </div>

      <div className="mt-10">
        <ChangePasswordCard />
      </div>
    </Shell>
  );
}
